import styled from "styled-components";

import { colorsPalettes, colorsSets, setFontVariant } from "@similarweb/styles";
import { IconButton } from "@similarweb/ui-components";
import { Flex, Stack } from "@similarweb/ui-components/foundations";

import type { ICampaignPromoModalTheme } from "./types";

const isWithFeatures = (theme: ICampaignPromoModalTheme): boolean => theme.layout === "withFeatures";

export const CtaButton = styled.button<{ $fullWidth?: boolean; $color?: string }>`
    ${({ $color }) => setFontVariant("button", $color ?? colorsSets.marketingGenAI.secondary)};
    background: ${colorsPalettes.carbon[0]};
    border: none;
    border-radius: 88px;
    padding: 8px 16px;
    cursor: pointer;
    width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "fit-content")};
`;

const getGradientValue = (theme: ICampaignPromoModalTheme): string => {
    if (theme.background) {
        return theme.background;
    }
    const position = theme.gradientPosition ?? "center";
    const gradientParams = position === "center" ? "100% 50% at 50% 38%" : "60% 60% at 26% 45%";
    return `radial-gradient(${gradientParams}, ${theme.backgroundBlurColor} 1.69%, rgba(0, 9, 33, 0.7) 34.13%)`;
};

export const ContentWrapper = styled(Flex)<{ $theme: ICampaignPromoModalTheme }>`
    position: relative;
    justify-content: space-between;
    align-items: center;
    padding: ${({ $theme }) => (isWithFeatures($theme) ? "15px 20px 15px 0" : "27px  0")};
    width: 100%;
    height: 100%;
    background: ${({ $theme }) => getGradientValue($theme)};
    border-radius: 4px;
`;

export const CloseIconButton = styled(IconButton)`
    position: absolute;
    top: 16px;
    right: 16px;
    .SWReactIcons svg {
        path {
            fill: ${({ iconColor }) => `${iconColor} !important`};
        }
    }
`;

export const Title = styled.span<{ $theme: ICampaignPromoModalTheme }>`
    text-transform: ${({ $theme }) => $theme.titleStyle ?? "uppercase"};
    text-align: center;
    font-feature-settings:
        "liga" off,
        "clig" off;
    font-style: normal;
    ${({ $theme }) => setFontVariant($theme.titleFontVariant ?? "h1", colorsPalettes.carbon[0])};
    span {
        ${({ $theme }) => setFontVariant($theme.titleSpanFontVariant ?? "h1", colorsPalettes.carbon[0])};
        ${({ $theme }) => ($theme.titleSpanFontWeight != null ? `font-weight: ${$theme.titleSpanFontWeight};` : "")};
    }
`;

export const Image = styled.img<{ $theme: ICampaignPromoModalTheme }>`
    position: absolute;
    width: 116px;
    height: 116px;
    align-self: center;
    margin-top: ${({ $theme }) => `calc(${isWithFeatures($theme) ? "20%" : "35%"} - (116px / 2))`};
`;

export const LeftContent = styled(Stack)<{ $theme: ICampaignPromoModalTheme }>`
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: 100%;
    padding: ${({ $theme }) => (isWithFeatures($theme) ? "25px 45px 15px" : "0")};
`;

export const RightContent = styled(Stack)<{ $theme: ICampaignPromoModalTheme }>`
    justify-content: space-between;
    align-items: flex-start;
    width: 85%;
    height: 100%;
    background: ${({ $theme }) => $theme.featuresPanelBackground ?? colorsPalettes.midnight[500]};
    border-radius: 8px;
    padding: 12px 24px;
`;

export const CloseButton = styled.button`
    ${setFontVariant("button", colorsPalettes.carbon[0])};
    cursor: pointer;
    border: none;
    background: none;
    padding: 0;
`;
export const CampaignModalImage = styled.img<{ $size?: number }>`
    width: ${({ $size }) => `${$size ?? 140}px`};
    height: ${({ $size }) => `${$size ?? 140}px`};
`;

export const PercentageText = styled.span<{ $theme: ICampaignPromoModalTheme }>`
    text-align: center;
    ${({ $theme }) =>
        setFontVariant(
            $theme.percentageFontVariant ?? "h1",
            $theme.percentageFontColor ?? colorsPalettes.bluegrey[100],
        )}
    > span {
        ${setFontVariant("boldH3", colorsPalettes.orange[300])}
    }
`;
